// web/src/services/analytics.ts
// Mixpanel integration for web - mirrors mobile analytics for unified tracking

import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || '';

// Disable analytics in dev so test sessions don't pollute Mixpanel.
// Override with REACT_APP_ANALYTICS_IN_DEV=true if you need to test analytics locally.
const ANALYTICS_DISABLED =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_ANALYTICS_IN_DEV !== 'true';

let isInitialized = false;

/**
 * Initialize Mixpanel - call once at app startup
 */
export function initializeAnalytics(): void {
  if (ANALYTICS_DISABLED) {
    console.log('[Analytics] Disabled in dev (set REACT_APP_ANALYTICS_IN_DEV=true to enable)');
    return;
  }
  if (isInitialized || !MIXPANEL_TOKEN) {
    console.log('[Analytics] Skipping init - no token or already initialized');
    return;
  }

  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: process.env.NODE_ENV === 'development',
      track_pageview: true,
      persistence: 'localStorage',
    });

    // Set platform to distinguish from mobile
    mixpanel.register({ platform: 'web' });

    isInitialized = true;
    console.log('[Analytics] Mixpanel initialized');
  } catch (error) {
    console.log('[Analytics] Init failed:', error);
  }
}

/**
 * Identify user after login/registration
 */
export function identifyUser(userId: string | number, properties?: Record<string, any>): void {
  if (!isInitialized) return;

  mixpanel.identify(userId.toString());

  if (properties) {
    mixpanel.people.set(properties);
  }
}

/**
 * Set user properties (email, name, plan, etc.)
 */
export function setUserProperties(properties: Record<string, any>): void {
  if (!isInitialized) return;
  mixpanel.people.set(properties);
}

/**
 * Track an event
 */
export function trackEvent(eventName: string, properties?: Record<string, any>): void {
  if (!isInitialized) return;
  mixpanel.track(eventName, properties);
}

/**
 * Track page/screen view
 */
export function trackScreen(screenName: string, properties?: Record<string, any>): void {
  if (!isInitialized) return;
  mixpanel.track('Screen View', { screen_name: screenName, ...properties });
}

/**
 * Reset user on logout
 */
export function resetAnalytics(): void {
  if (!isInitialized) return;
  mixpanel.reset();
}

// Pre-defined event names for consistency (matches mobile)
export const AnalyticsEvents = {
  // Auth events
  SIGN_UP: 'Sign Up',
  LOGIN: 'Login',
  LOGOUT: 'Logout',

  // Onboarding events
  ONBOARDING_START: 'Onboarding Start',
  ONBOARDING_STEP_VIEWED: 'Onboarding Step Viewed',
  ONBOARDING_STEP_COMPLETED: 'Onboarding Step Completed',
  ONBOARDING_COMPLETE: 'Onboarding Complete',
  FAMILY_MEMBER_ADDED: 'Family Member Added',

  // Meal plan events
  MEAL_PLAN_GENERATED: 'Meal Plan Generated',
  MEAL_PLAN_APPROVED: 'Meal Plan Approved',
  MEAL_REPLACED: 'Meal Replaced',

  // Recipe events
  RECIPE_VIEWED: 'Recipe Viewed',
  RECIPE_FAVORITED: 'Recipe Favorited',
  RECIPE_UNFAVORITED: 'Recipe Unfavorited',
  RECIPE_DISLIKED: 'Recipe Disliked',

  // Shopping list events
  SHOPPING_LIST_VIEWED: 'Shopping List Viewed',
  SHOPPING_ITEM_CHECKED: 'Shopping Item Checked',

  // Subscription events
  PAYWALL_VIEWED: 'Paywall Viewed',
  SUBSCRIPTION_STARTED: 'Subscription Started',
  SUBSCRIPTION_CANCELLED: 'Subscription Cancelled',

  // Feature usage
  PANTRY_UPDATED: 'Pantry Updated',
  FEEDBACK_SUBMITTED: 'Feedback Submitted',
};
